<script setup lang="ts">
import {
  displayFrom,
  hexAlpha,
  levelTiers,
  mixColor,
  type LevelTier,
} from '@/utils/levels';
import type { TierState } from '@/composables/useLevelsProgress';

const props = defineProps<{
  tier: LevelTier;
  state: TierState;
  prevTier: LevelTier | null;
  nextTier: LevelTier | null;
  /** Índice do elo do jogador — decide quais vizinhos saem acinzentados. */
  playerTierIndex: number;
  viewIndex: number;
  /** Rótulo do nível do jogador, para a plaqueta "NÍVEL ATUAL". */
  playerLevelLabel: string;
}>();

const emit = defineEmits<{ step: [direction: number] }>();

const isLocked = computed(() => props.state === 'locked');

/** "Sem elo" e VIP não ganham o prefixo "Nível" — só os elos abertos. */
const title = computed(() =>
  props.tier.isVip || props.tier.isUnranked
    ? props.tier.name
    : `Nível ${props.tier.name}`,
);

const badgeFilter = computed(() =>
  isLocked.value
    ? `saturate(.55) brightness(.72) drop-shadow(0 0 14px ${hexAlpha(props.tier.color, 0.5)})`
    : `drop-shadow(0 0 26px ${props.tier.color})`,
);

const plaque = computed(() => {
  if (props.tier.isVip) return 'VIP';

  // O degrau "Sem elo" é um nível só (o 0) — sem faixa.
  if (props.tier.isUnranked) return 'LV 0';

  if (props.state === 'current') {
    return props.playerLevelLabel.replace('Nível ', 'LV ');
  }

  // `displayFrom`: o Bronze se apresenta como 1–4 — o nível 0 é o degrau
  // "Sem elo" e não entra na plaqueta de elo nenhum.
  return `LV ${displayFrom(props.tier)}–${props.tier.to}`;
});

/** Vizinho: aceso se já alcançado, acinzentado se ainda bloqueado. */
function neighbourFilter(tier: LevelTier | null, index: number) {
  if (!tier) return 'grayscale(1) brightness(.5)';

  return index > props.playerTierIndex
    ? 'grayscale(.75) brightness(.7)'
    : `drop-shadow(0 0 10px ${hexAlpha(tier.color, 0.5)})`;
}

const prevIndex = computed(() => props.viewIndex - 1);
const nextIndex = computed(() => props.viewIndex + 1);

const prevLocked = computed(
  () => !!props.prevTier && prevIndex.value > props.playerTierIndex,
);

const nextLocked = computed(
  () => !!props.nextTier && nextIndex.value > props.playerTierIndex,
);

/**
 * Enfeite de continuidade das pontas: no primeiro elo o vazio da esquerda vira
 * a arte do último, e no último o vazio da direita vira a arte do primeiro.
 * Não é loop — são só imagens, sem clique, sem foco e sem rótulo, e a navegação
 * continua travada nas pontas (as setas seguem desabilitadas).
 */
const ghostPrev = computed(() => {
  if (props.prevTier) return null;

  // Artes resolvidas pelo CDN aqui dentro: `levelTiers()` lê
  // `useRuntimeConfig()`, que só responde no contexto Nuxt.
  const tiers = levelTiers();

  return tiers[tiers.length - 1];
});

const ghostNext = computed(() => (props.nextTier ? null : levelTiers()[0]));

function hint(tier: LevelTier | null, index: number, fallback: string) {
  if (!tier) return fallback === 'anterior' ? 'Primeiro elo' : 'Último elo';

  return index === props.playerTierIndex ? 'atual' : fallback;
}
</script>

<template>
  <div class="flex flex-col gap-[15px]">
    <div
      class="relative -mb-1.5 flex items-center justify-center px-[9px] lg:px-[13px]"
    >
      <!-- Vizinho anterior -->
      <button
        v-if="prevTier"
        class="flex w-[76px] flex-none flex-col items-center gap-[3px] p-0"
        type="button"
        @click="emit('step', -1)"
      >
        <span
          class="relative h-[34px] w-[34px] bg-contain bg-center bg-no-repeat lg:h-12 lg:w-12"
          :style="{
            backgroundImage: `url(${prevTier.badge})`,
            filter: neighbourFilter(prevTier, prevIndex),
          }"
        >
          <LevelsLockDot v-if="prevLocked" />
        </span>
        <span
          class="w-full truncate text-center text-[11.5px] font-medium leading-[15px] text-white/[0.66]"
        >
          {{ prevTier.name }}
        </span>
        <span
          class="w-full whitespace-nowrap text-center text-[8.5px] font-bold uppercase leading-[11px] tracking-[0.14em]"
          :style="{
            color:
              prevIndex === playerTierIndex
                ? mixColor(tier.color, '#ffffff', 0.55)
                : 'rgba(255,255,255,.4)',
          }"
        >
          {{ hint(prevTier, prevIndex, 'anterior') }}
        </span>
      </button>
      <!-- Ponta esquerda: a arte do último elo, só de enfeite (ver `ghostPrev`) -->
      <span
        v-else
        aria-hidden="true"
        class="pointer-events-none flex w-[76px] flex-none flex-col items-center gap-[3px]"
      >
        <span
          class="h-[34px] w-[34px] bg-contain bg-center bg-no-repeat lg:h-12 lg:w-12"
          :style="{
            backgroundImage: `url(${ghostPrev?.badge})`,
            filter: 'grayscale(1) brightness(.5) opacity(.4)',
          }"
        ></span>
        <!-- As duas linhas de texto do vizinho de verdade, reservadas em branco:
             sem elas a arte da ponta ficaria mais baixa que a do outro lado. -->
        <span class="invisible text-[11.5px] leading-[15px]">&nbsp;</span>
        <span class="invisible text-[8.5px] leading-[11px]">&nbsp;</span>
      </span>

      <span
        class="mb-5 h-[2px] w-[14px] flex-none rounded-sm lg:w-7"
        :style="{
          background: `linear-gradient(90deg,${
            prevTier ? hexAlpha(prevTier.color, 0.7) : 'rgba(255,255,255,.14)'
          },${tier.color})`,
        }"
      ></span>

      <!-- Badge grande do elo em foco -->
      <div
        :key="tier.key"
        class="levels-hero relative flex h-[120px] w-[120px] flex-none items-center justify-center lg:h-[168px] lg:w-[168px]"
      >
        <span
          aria-hidden="true"
          class="levels-rays pointer-events-none absolute left-1/2 top-1/2 h-[151px] w-[151px] rounded-full lg:h-[212px] lg:w-[212px]"
          :style="{
            background: `repeating-conic-gradient(from 0deg,${hexAlpha(tier.color, 0.16)} 0 2.6deg,transparent 2.6deg 8deg)`,
            maskImage:
              'radial-gradient(#000 10%,rgba(0,0,0,.55) 40%,transparent 70%)',
            WebkitMaskImage:
              'radial-gradient(#000 10%,rgba(0,0,0,.55) 40%,transparent 70%)',
            opacity: tier.isVip ? 0.34 : 0.7,
          }"
        ></span>
        <span
          aria-hidden="true"
          class="levels-glow pointer-events-none absolute inset-0 rounded-full"
          :style="{
            background: `radial-gradient(${hexAlpha(tier.color, 0.3)} 0%,${hexAlpha(tier.color, 0.16)} 40%,transparent 68%)`,
          }"
        ></span>

        <span
          class="levels-badge relative z-[2] flex h-[74%] w-[74%] items-center justify-center"
        >
          <span
            class="levels-bob h-full w-full bg-contain bg-center bg-no-repeat"
            :style="{
              backgroundImage: `url(${tier.badge})`,
              filter: badgeFilter,
            }"
          ></span>
        </span>

        <span
          class="absolute bottom-[6%] left-1/2 z-[2] flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded px-[11px] py-[5px] text-[10px] font-bold leading-[13px] tracking-[0.09em] text-white"
          :style="{
            background: `linear-gradient(180deg,${mixColor(tier.color, '#04081C', 0.35)},${mixColor(tier.color, '#04081C', 0.62)})`,
            boxShadow: `inset 0 0 0 1px ${hexAlpha(tier.color, 0.6)},0 4px 12px -6px rgba(0,0,0,.9)`,
          }"
        >
          <NuxtIcon
            v-if="isLocked"
            class="text-[11px]"
            filled
            name="mini-games/locked"
          />
          {{ plaque }}
          <span
            v-if="state === 'current'"
            class="whitespace-nowrap rounded-[3px] bg-white/[0.92] px-1.5 py-0.5 text-[8px] font-bold leading-[11px] tracking-[0.1em]"
            :style="{ color: mixColor(tier.color, '#050A18', 0.45) }"
          >
            NÍVEL ATUAL
          </span>
        </span>
      </div>

      <span
        class="mb-5 h-[2px] w-[14px] flex-none rounded-sm lg:w-7"
        :style="{
          background: `linear-gradient(90deg,${tier.color},${
            nextTier ? hexAlpha(nextTier.color, 0.5) : 'rgba(255,255,255,.14)'
          })`,
        }"
      ></span>

      <!-- Vizinho seguinte -->
      <button
        v-if="nextTier"
        class="flex w-[76px] flex-none flex-col items-center gap-[3px] p-0"
        type="button"
        @click="emit('step', 1)"
      >
        <span
          class="relative h-[34px] w-[34px] bg-contain bg-center bg-no-repeat lg:h-12 lg:w-12"
          :style="{
            backgroundImage: `url(${nextTier.badge})`,
            filter: neighbourFilter(nextTier, nextIndex),
          }"
        >
          <LevelsLockDot v-if="nextLocked" />
        </span>
        <span
          class="w-full truncate text-center text-[11.5px] font-medium leading-[15px] text-white/[0.66]"
        >
          {{ nextTier.name }}
        </span>
        <span
          class="w-full whitespace-nowrap text-center text-[8.5px] font-bold uppercase leading-[11px] tracking-[0.14em]"
          :style="{
            color:
              nextIndex === playerTierIndex
                ? mixColor(tier.color, '#ffffff', 0.55)
                : 'rgba(255,255,255,.46)',
          }"
        >
          {{ hint(nextTier, nextIndex, 'próximo') }}
        </span>
      </button>
      <!-- Ponta direita: a arte do primeiro elo, só de enfeite (ver `ghostNext`) -->
      <span
        v-else
        aria-hidden="true"
        class="pointer-events-none flex w-[76px] flex-none flex-col items-center gap-[3px]"
      >
        <span
          class="h-[34px] w-[34px] bg-contain bg-center bg-no-repeat lg:h-12 lg:w-12"
          :style="{
            backgroundImage: `url(${ghostNext?.badge})`,
            filter: 'grayscale(1) brightness(.5) opacity(.4)',
          }"
        ></span>
        <span class="invisible text-[11.5px] leading-[15px]">&nbsp;</span>
        <span class="invisible text-[8.5px] leading-[11px]">&nbsp;</span>
      </span>
    </div>

    <!--
      Título + setas. Fileira de largura própria com as setas presas nas pontas:
      com `justify-center` a posição delas seguia a largura do nome do elo e
      elas andavam a cada troca. O título fica no meio com `flex-1`, e como as
      duas setas têm a mesma largura a sobra do centro é simétrica.
    -->
    <div class="flex items-center justify-between gap-3.5 px-1.5 lg:px-2.5">
      <button
        aria-label="Elo anterior"
        class="flex h-7 w-7 flex-none items-center justify-center rounded-full text-white/[0.55] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)]"
        :class="prevTier ? 'opacity-100' : 'opacity-30'"
        :disabled="!prevTier"
        type="button"
        @click="emit('step', -1)"
      >
        <LevelsNavArrow
          class="h-[15px] w-[15px]"
          direction="prev"
        />
      </button>
      <span
        :key="tier.key"
        class="levels-title min-w-0 flex-1 truncate text-center text-base font-bold leading-[1.18] tracking-[-0.035em] text-white lg:text-[22px]"
      >
        {{ title }}
      </span>
      <button
        aria-label="Próximo elo"
        class="flex h-7 w-7 flex-none items-center justify-center rounded-full text-white/[0.55] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)]"
        :class="nextTier ? 'opacity-100' : 'opacity-30'"
        :disabled="!nextTier"
        type="button"
        @click="emit('step', 1)"
      >
        <LevelsNavArrow
          class="h-[15px] w-[15px]"
          direction="next"
        />
      </button>
    </div>
  </div>
</template>

<style scoped lang="postcss">
/* `transition` é zerada globalmente no main.scss, então tudo aqui é keyframe.
   A `:key` no badge e no título remonta o nó a cada troca de elo, o que
   reinicia as animações de entrada. */
@keyframes levelsBadgeIn {
  0% {
    opacity: 0;
    transform: scale(0.84) rotate(-4deg);
  }
  58% {
    transform: scale(1.045) rotate(1deg);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}

.levels-badge {
  animation: levelsBadgeIn 0.6s cubic-bezier(0.2, 0.75, 0.3, 1) both;
}

@keyframes levelsTitleIn {
  0% {
    opacity: 0;
    transform: translateY(8px);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}

.levels-title {
  animation: levelsTitleIn 0.45s cubic-bezier(0.2, 0.75, 0.3, 1) both;
}

@keyframes levelsBob {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

.levels-bob {
  animation: levelsBob 5s ease-in-out infinite;
}

@keyframes levelsGlowPulse {
  0%,
  100% {
    opacity: 0.32;
  }
  50% {
    opacity: 0.8;
  }
}

.levels-glow {
  animation: levelsGlowPulse 4.2s ease-in-out infinite;
}

@keyframes levelsSpinSlow {
  0% {
    transform: translate(-50%, -50%) rotate(0);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.levels-rays {
  transform: translate(-50%, -50%);
  transform-origin: 50% 50%;
  animation: levelsSpinSlow 90s linear infinite;
}
</style>
